// GoBD: Nachweis der Unveränderbarkeit und Datenträgerüberlassung für die
// Betriebsprüfung.
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useState } from 'react';
import { token } from '../../../gemeinsam/lib/api';
import { useDaten, datumZeit, euro } from '../../../gemeinsam/lib/daten';
import { Abzeichen, Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Status = {
  kette: { unversehrt: boolean; anzahl: number; letzterHash?: string | null; bruchBei?: number; grund?: string };
  belege: { gesamt: number; aeltester: string | null };
  aufbewahrungJahre: number;
  hinweise: string[];
};

type Eintrag = {
  nr: number; zeitpunkt: string; vorgang: string;
  belegNummer: string | null; belegId: string | null;
  betrag: number | null; benutzerId: string | null;
  vorgaengerHash: string; hash: string;
};

type Datei = { datei: string; name: string; zeilen: number | null };

const jahr = new Date().getFullYear();

export default function GoBD() {
  const [von, setVon] = useState(`${jahr}-01-01`);
  const [bis, setBis] = useState(new Date().toISOString().slice(0, 10));

  const { daten: status, laedt, fehler, neuLaden } = useDaten<Status>('/gobd/status');
  const { daten: protokoll } = useDaten<{ eintraege: Eintrag[]; gesamt: number }>('/gobd/protokoll?limit=50');
  const { daten: export_ } = useDaten<{ dateien: Datei[] }>(
    `/gobd/export?von=${von}&bis=${bis}`, [von, bis]);

  const [meldung, setMeldung] = useState<string | null>(null);

  async function herunterladen(datei: string) {
    setMeldung(null);
    try {
      const antwort = await fetch(apiUrl(`/api/gobd/export/${datei}?von=${von}&bis=${bis}`), {
        headers: { Authorization: `Bearer ${token() ?? ''}` },
      });
      if (!antwort.ok) throw new Error(String(antwort.status));

      // Als Datei speichern statt im Fenster anzeigen — der Prüfer bekommt sie
      // auf einen Datenträger.
      const blob = await antwort.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = datei;
      a.click();
      URL.revokeObjectURL(url);
      setMeldung(`${datei} wurde heruntergeladen.`);
    } catch {
      setMeldung(null);
    }
  }

  if (laedt && !status) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (fehler) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (!status) return null;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">GoBD &amp; Betriebsprüfung</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Nachweis der Unveränderbarkeit und Daten für den Prüfer
        </p>
      </div>

      <Erfolgsmeldung text={meldung} />

      <Karte klasse={status.kette.unversehrt
        ? 'border-emerald-300 dark:border-emerald-800'
        : 'border-rose-400 dark:border-rose-700'}>
        <KartenKopf
          titel="Unveränderbarkeit"
          hinweis={`${status.kette.anzahl} protokollierte Vorgänge`}
          aktion={<Knopf onClick={() => neuLaden()}>Erneut prüfen</Knopf>}
        />
        <div className="p-5">
          {status.kette.unversehrt ? (
            <Hinweis art="erfolg" titel="Protokollkette ist unversehrt">
              Alle {status.kette.anzahl} Einträge sind lückenlos verkettet. Es wurde nichts
              nachträglich verändert, entfernt oder eingefügt.
              {status.kette.letzterHash && (
                <span className="mt-2 block break-all font-mono text-xs opacity-70">
                  Letzter Prüfwert: {status.kette.letzterHash}
                </span>
              )}
            </Hinweis>
          ) : (
            <Hinweis art="fehler" titel={`Kette gebrochen bei Eintrag ${status.kette.bruchBei}`}>
              {status.kette.grund} Bitte umgehend prüfen — dieser Zustand ist gegenüber dem
              Finanzamt erklärungsbedürftig.
            </Hinweis>
          )}

          <ul className="mt-4 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {status.hinweise.map(h => (
              <li key={h} className="flex gap-2">
                <span className="text-emerald-600 dark:text-emerald-400" aria-hidden>✓</span>{h}
              </li>
            ))}
          </ul>
        </div>
      </Karte>

      <Karte>
        <KartenKopf
          titel="Datenträgerüberlassung (GDPdU)"
          hinweis="Diese Dateien übergeben Sie dem Prüfer"
        />
        <div className="space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Feld label="Zeitraum von" type="date" value={von} onChange={e => setVon(e.target.value)} />
            <Feld label="bis" type="date" value={bis} onChange={e => setBis(e.target.value)} />
          </div>

          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
            {(export_?.dateien ?? []).map(d => (
              <li key={d.datei} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm text-slate-900 dark:text-slate-100">{d.datei}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {d.name}{d.zeilen !== null && ` · ${d.zeilen} Datensätze`}
                  </p>
                </div>
                <Knopf variante="still" className="!text-xs" onClick={() => herunterladen(d.datei)}>
                  Herunterladen
                </Knopf>
              </li>
            ))}
          </ul>

          <Hinweis art="info">
            Laden Sie alle Dateien herunter und geben Sie sie gemeinsam weiter. Die{' '}
            <span className="font-mono">index.xml</span> beschreibt den Aufbau der CSV-Dateien nach
            dem Beschreibungsstandard des Bundesfinanzministeriums — Prüfsoftware wie IDEA liest sie
            damit ohne Rückfragen ein.
          </Hinweis>
        </div>
      </Karte>

      <Karte>
        <KartenKopf
          titel="Änderungsprotokoll"
          hinweis={protokoll ? `${protokoll.gesamt} Einträge insgesamt, neueste zuerst` : undefined}
        />
        {!protokoll?.eintraege.length ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            Noch keine buchungsrelevanten Vorgänge.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Nr</th>
                  <th className="px-3 py-2.5 font-medium">Zeitpunkt</th>
                  <th className="px-3 py-2.5 font-medium">Vorgang</th>
                  <th className="px-3 py-2.5 font-medium">Beleg</th>
                  <th className="px-3 py-2.5 text-right font-medium">Betrag</th>
                  <th className="px-5 py-2.5 font-medium">Prüfwert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {protokoll.eintraege.map(e => (
                  <tr key={e.nr}>
                    <td className="px-5 py-2 text-slate-500 dark:text-slate-400">{e.nr}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">{datumZeit(e.zeitpunkt)}</td>
                    <td className="px-3 py-2">
                      <Abzeichen art={e.vorgang.includes('storniert') ? 'rot'
                        : e.vorgang.includes('bezahlt') ? 'gruen'
                        : e.vorgang.includes('export') ? 'blau' : 'neutral'}>
                        {e.vorgang}
                      </Abzeichen>
                    </td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{e.belegNummer || '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-900 dark:text-slate-100">
                      {e.betrag != null ? euro(e.betrag) : '—'}
                    </td>
                    <td className="px-5 py-2 font-mono text-xs text-slate-400 dark:text-slate-500"
                        title={e.hash}>{e.hash.slice(0, 12)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
    </div>
  );
}
